import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ClaimOffer,
  JoinAck,
  Json,
  JsonObject,
  RequestId,
  ServerToClientEvents,
  Snapshot,
} from '@gsf/contract';

/**
 * The session socket. Holds the `Snapshot` opaquely: reading `shared`/`private` is the
 * game's job. The join is replayed on every reconnect so a phone waking from sleep resumes.
 */

type SessionSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/**
 * A host presents nothing: the `HttpOnly` login cookie rides the same-origin handshake.
 * A display's `displayToken` is optional because a signed-in host's cookie also admits it.
 */
export type JoinParams =
  | { as: 'host'; code: string }
  | { as: 'display'; code: string; displayToken?: string }
  | {
      as: 'player';
      code: string;
      name: string;
      /** The credential in `localStorage`, if this device has one. */
      token?: string;
      claimParticipantId?: string;
      forceNew?: boolean;
    };

/**
 * The latest `session:event` under each name.
 *
 * `n` is a delivery counter, not a clock: two identical payloads a millisecond apart
 * must still read as two events, or ruling the same player wrong twice in a row would
 * flash the stage once. A timestamp cannot promise that; a counter can.
 */
export interface EventStamp {
  readonly payload: JsonObject | null;
  readonly n: number;
}

export type SessionEvents = Readonly<Record<string, EventStamp>>;

export interface UseSession {
  snapshot: Snapshot | null;
  connected: boolean;
  joined: boolean;
  /** Fatal-ish: the join was refused (bad code, not signed in, wrong TV link). */
  joinError: string | null;
  /** Transient: an action was rejected, or the server said something. Clears itself. */
  toast: string | null;
  /** The most recent one-shot under each name. See `EventStamp`. */
  events: SessionEvents;
  /**
   * The server is asking whether this device is a participant who dropped off,
   * rather than a new one. Held here rather than returned from `join()` alone
   * because it can also arrive on an auto-join replay, which has no caller.
   */
  claimOffer: ClaimOffer | null;
  /** The host removed this phone: from the join ack, or live (`core:kicked`). */
  kicked: boolean;
  join: (params: JoinParams) => Promise<JoinAck>;
  /** One game action. `type` is the game's own; the envelope is core's. */
  send: (type: string, payload?: JsonObject) => void;
  /** One answer to an open request; core checks it against the request that asked. */
  submit: (requestId: RequestId, value: Json) => void;
  /**
   * Show a message in the same toast slot as server errors, for HTTP failures that have
   * no socket to arrive on. `GameUiProps.notify` is wired here.
   */
  notify: (message: string) => void;
  clearToast: () => void;
}

/**
 * Per-action id the server dedupes on. `randomUUID` is secure-context only, so it is
 * undefined on phones joining over plain-http LAN; `getRandomValues` works everywhere.
 */
function actionId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * One socket per view, with the join re-played automatically on every reconnect.
 *
 * Pass `autoJoin` for views that can join immediately (the display, and the host once
 * it is signed in); the player view calls `join()` from its name form instead.
 */
export function useSession(autoJoin?: JoinParams): UseSession {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [connected, setConnected] = useState(false);
  const [joined, setJoined] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [events, setEvents] = useState<SessionEvents>({});
  const [claimOffer, setClaimOffer] = useState<ClaimOffer | null>(null);
  const [kicked, setKicked] = useState(false);

  const socketRef = useRef<SessionSocket | null>(null);
  // Held in a ref so the 'connect' handler always has the newest credentials
  // without needing to re-subscribe.
  const joinRef = useRef<JoinParams | null>(autoJoin ?? null);
  const onAckRef = useRef<((ack: JoinAck) => void) | null>(null);
  const counter = useRef(0);

  const emitJoin = useCallback((socket: SessionSocket, params: JoinParams) => {
    const handle = (ack: JoinAck) => {
      setJoined(ack.ok);
      setKicked(Boolean(ack.kicked));
      setClaimOffer(ack.claimOffer ?? null);
      // A claim offer isn't a refusal — the player is being asked a question,
      // and answering it is the whole point of the screen they're on.
      setJoinError(ack.ok || ack.claimOffer ? null : (ack.error ?? 'Could not join.'));
      onAckRef.current?.(ack);
      onAckRef.current = null;
    };

    if (params.as === 'player') {
      socket.emit(
        'session:join',
        {
          code: params.code,
          as: 'player',
          name: params.name,
          resume: params.token,
          claimParticipantId: params.claimParticipantId,
          forceNew: params.forceNew,
        },
        handle,
      );
      return;
    }

    // A host sends no credential: the login cookie rides on the handshake.
    socket.emit(
      'session:join',
      {
        code: params.code,
        as: params.as,
        credentials: params.as === 'display' && params.displayToken
          ? { displayToken: params.displayToken }
          : undefined,
      },
      handle,
    );
  }, []);

  useEffect(() => {
    const socket: SessionSocket = io({ transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      if (joinRef.current) emitJoin(socket, joinRef.current);
    });
    socket.on('disconnect', () => {
      setConnected(false);
      setJoined(false);
    });
    socket.on('session:snapshot', setSnapshot);
    socket.on('session:error', ({ message }) => setToast(message));
    socket.on('session:notice', ({ message }) => setToast(message));
    socket.on('session:event', ({ name, payload }) => {
      // The server has already unseated this socket; nothing more will arrive for it.
      if (name === 'core:kicked') {
        setKicked(true);
        setJoined(false);
        return;
      }
      counter.current += 1;
      const n = counter.current;
      setEvents((current) => ({ ...current, [name]: { payload, n } }));
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [emitJoin]);

  // Keep the stored credentials current when a parent passes new ones (e.g. the TV
  // link's token arrives), and join right away if we're already connected.
  const autoKey = autoJoin ? JSON.stringify(autoJoin) : null;
  useEffect(() => {
    if (!autoJoin) return;
    joinRef.current = autoJoin;
    const socket = socketRef.current;
    if (socket?.connected) emitJoin(socket, autoJoin);
    // autoKey stands in for a deep compare of autoJoin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoKey, emitJoin]);

  const join = useCallback(
    (params: JoinParams) =>
      new Promise<JoinAck>((resolve) => {
        joinRef.current = params;
        onAckRef.current = resolve;
        const socket = socketRef.current;
        if (socket?.connected) {
          emitJoin(socket, params);
        }
        // Not connected yet: the 'connect' handler will replay this join and
        // the stored resolver fires then.
      }),
    [emitJoin],
  );

  /**
   * Fire an action. Rejections surface via `session:error` (the toast); the ack carries
   * the resulting `seq`, which nothing reads yet.
   */
  const send = useCallback((type: string, payload?: JsonObject) => {
    socketRef.current?.emit('session:action', { actionId: actionId(), type, payload }, () => {
      // Deliberately empty. See above.
    });
  }, []);

  /** The same two channels, for an answer rather than an action. See `send`. */
  const submit = useCallback((requestId: RequestId, value: Json) => {
    socketRef.current?.emit(
      'session:submit',
      { actionId: actionId(), requestId, value },
      () => {
        // Deliberately empty. See `send`.
      },
    );
  }, []);

  // Toasts are informational; don't make the host dismiss them mid-game.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const clearToast = useCallback(() => setToast(null), []);
  const notify = useCallback((message: string) => setToast(message), []);

  return {
    snapshot,
    connected,
    joined,
    joinError,
    toast,
    events,
    claimOffer,
    kicked,
    join,
    send,
    submit,
    notify,
    clearToast,
  };
}

/**
 * Seconds left on a server-set deadline, corrected for phone clock skew.
 * Returns null when no timer is running.
 */
export function useCountdown(endsAt: number | null, serverNow: number | null): number | null {
  const [, tick] = useState(0);
  const skewRef = useRef(0);

  // Re-sync the skew only when the server hands us a fresh clock sample. Recomputing it on every
  // render would cancel the tick below — `Date.now() + skew` would always equal the frozen
  // `serverNow`, so the display froze between snapshots (a quiet countdown stuck on its first value).
  useEffect(() => {
    if (serverNow !== null) skewRef.current = serverNow - Date.now();
  }, [serverNow]);

  useEffect(() => {
    if (endsAt === null) return;
    const id = setInterval(() => tick((n) => n + 1), 200);
    return () => clearInterval(id);
  }, [endsAt]);

  if (endsAt === null) return null;
  return Math.max(0, Math.ceil((endsAt - (Date.now() + skewRef.current)) / 1000));
}
